import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { decideRequest } from "@/lib/approvals";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { since } from "@/lib/lastSeen";

// "2026-09" -> "September". The payout tile says which month it is
// counting, because the figure now changes when a payslip is edited.
const monthName = (m) => {
  if (!m) return "";
  const d = new Date(`${m}-01T00:00:00`);
  return isNaN(d) ? m : d.toLocaleDateString("en-IN", { month: "long" });
};
const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const dmy = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return d; } };

function Tile({ label, value, active, tone, onClick }) {
  const tones = {
    accent: "bg-emerald-50 text-emerald-700",
    warn: "bg-amber-50 text-amber-700",
    plain: "bg-zinc-50 text-zinc-900",
  };
  return (
    <button onClick={onClick} data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`text-left rounded-lg p-3 transition ${tones[tone] || tones.plain} ${active ? "ring-2 ring-emerald-500" : "hover:brightness-95"}`}>
      <div className="text-[11px] opacity-70">{label}</div>
      <div className="text-2xl font-semibold leading-tight mt-0.5">{value}</div>
    </button>
  );
}

function Section({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-zinc-200 shadow-none">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <span>
          <span className="text-[15px] font-medium text-zinc-900">{title}</span>
          {subtitle && <span className="ml-2 text-[11px] text-zinc-500">{subtitle}</span>}
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>
      {open && <CardContent className="px-5 pb-5 pt-0">{children}</CardContent>}
    </Card>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-zinc-500 py-3">{children}</p>;
}

function BasicDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/dashboard/stats"); setS(data); } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);
  if (!s) return <div className="text-sm text-zinc-500">Loading…</div>;
  const cards = [
    ["Team size", s.headcount], ["Present today", s.present_today],
    ["Pending leaves", s.pending_leaves], ["Regularizations", s.pending_regularizations],
  ].filter(([, v]) => v !== undefined);
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs text-zinc-500">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Overview</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(([k, v]) => (
          <Card key={k} className="border-zinc-200 shadow-none"><CardContent className="p-4">
            <div className="text-xs text-zinc-500">{k}</div>
            <div className="text-2xl font-semibold text-zinc-900 mt-1">{v}</div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("present");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const { data } = await api.get("/dashboard/overview");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isAdmin]);

  if (!isAdmin) return <BasicDashboard />;

  const decide = async (a, approve) => {
    setBusy(a.id);
    try {
      await decideRequest(a.kind, a.id, approve);
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!data) return <div className="text-sm text-zinc-500">Could not load dashboard.</div>;

  const t = data.tiles, ex = data.exceptions, ppl = data.people;
  const filtered = (data.rows || []).filter((r) =>
    view === "all" ? true
    : view === "present" ? r.status === "present"
    : view === "not_in" ? (r.status === "not_in" || r.status === "absent")
    : view === "leave" ? r.status === "leave"
    : view === "half_day" ? r.status === "half_day" : true);

  const label = { present: "Present today", not_in: "Not checked in", leave: "On leave", half_day: "Half day", all: "Whole team" }[view];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs text-zinc-500">{new Date(data.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Overview</h1>
        </div>
        <Button variant="ghost" onClick={load} className="h-9"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <Tile label="Present" value={t.present} tone="accent" active={view === "present"} onClick={() => setView("present")} />
          <Tile label="Not in yet" value={t.not_in} active={view === "not_in"} onClick={() => setView("not_in")} />
          <Tile label="On leave" value={t.on_leave} active={view === "leave"} onClick={() => setView("leave")} />
          <Tile label="Half day" value={t.half_day} active={view === "half_day"} onClick={() => setView("half_day")} />
          <Tile label="Headcount" value={t.headcount} active={view === "all"} onClick={() => setView("all")} />
          <Tile label="Approvals" value={t.approvals} tone="warn" onClick={() => document.getElementById("approvals")?.scrollIntoView({ behavior: "smooth" })} />
        </div>

        <div className="mt-4 rounded-lg border border-emerald-200 overflow-hidden">
          <div className="bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800">{label} · {filtered.length}</div>
          {filtered.length === 0 ? <div className="px-3 py-6 text-center text-sm text-zinc-500">Nobody here.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-zinc-500">
                  <th className="px-3 py-2 font-normal">Name</th><th className="px-2 py-2 font-normal">In</th>
                  <th className="px-2 py-2 font-normal">Out</th><th className="px-2 py-2 font-normal">Hrs</th>
                  <th className="px-3 py-2 font-normal">Task given</th><th className="px-3 py-2 font-normal">Task completed</th>
                </tr></thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.employee_id} onClick={() => navigate(`/employees/${r.employee_id}`)}
                        className="border-t border-zinc-100 hover:bg-zinc-50/60 cursor-pointer">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium text-zinc-900">{r.name}</div>
                        <div className="text-[11px] text-zinc-500">{r.employee_code} · {r.department || "—"}</div>
                      </td>
                      <td className="px-2 py-2 tabular-nums">{r.check_in || "—"}</td>
                      <td className="px-2 py-2 tabular-nums">{r.check_out || "—"}</td>
                      <td className="px-2 py-2 tabular-nums">{r.work_hours || 0}</td>
                      <td className="px-3 py-2 text-zinc-600 max-w-[220px] truncate" title={r.tasks_given || ""}>{r.tasks_given || "—"}</td>
                      <td className="px-3 py-2 text-zinc-600 max-w-[220px] truncate" title={r.tasks_completed || ""}>{r.tasks_completed || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent></Card>

      <Card className="border-zinc-200 shadow-none" id="approvals"><CardContent className="p-5">
        <h2 className="text-[15px] font-medium text-zinc-900">Needs attention</h2>
        <p className="text-[11px] text-zinc-500 mb-3">Exceptions only — empty when all is well</p>

        <div className="grid md:grid-cols-2 gap-2">
          <div className="border-l-[3px] border-amber-400 bg-amber-50 px-3 py-2">
            <div className="text-[11px] text-amber-800 mb-1">Late arrivals (after 10:15)</div>
            {ex.late.length ? ex.late.map((x, i) => <div key={i} className="text-[12px] text-amber-900">{x.name} · {x.check_in}</div>) : <div className="text-[12px] text-amber-900/60">None</div>}
          </div>
          <div className="border-l-[3px] border-red-400 bg-red-50 px-3 py-2">
            <div className="text-[11px] text-red-800 mb-1">Missing task logs</div>
            {ex.missing_task_logs.length ? ex.missing_task_logs.map((x, i) => <div key={i} className="text-[12px] text-red-900">{x.name} · checked in {x.check_in}</div>) : <div className="text-[12px] text-red-900/60">None</div>}
          </div>
          <div className="border-l-[3px] border-zinc-300 bg-zinc-50 px-3 py-2">
            <div className="text-[11px] text-zinc-600 mb-1">Long days this month (10 hrs+)</div>
            {ex.long_days.length ? ex.long_days.map((x, i) => <div key={i} className="text-[12px] text-zinc-700">{x.name} · {x.days} days</div>) : <div className="text-[12px] text-zinc-500">None</div>}
          </div>
          <div className="border-l-[3px] border-zinc-300 bg-zinc-50 px-3 py-2">
            <div className="text-[11px] text-zinc-600 mb-1">Attendance gaps</div>
            {ex.gaps.length ? ex.gaps.slice(0, 4).map((x, i) => <div key={i} className="text-[12px] text-zinc-700">{x.name} · {dmy(x.date)} · {x.issue}</div>) : <div className="text-[12px] text-zinc-500">None</div>}
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-zinc-200 px-3 py-2">
          <div className="text-[11px] text-zinc-600 mb-2">Pending approvals</div>
          {!data.approvals.length ? <div className="text-[12px] text-zinc-500 py-1">Nothing waiting.</div> :
            data.approvals.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 py-1.5 border-t border-zinc-100 first:border-0">
                <div className="min-w-0">
                  <div className="text-[13px] text-zinc-900 truncate">{a.name} · {a.detail}</div>
                  {a.reason && <div className="text-[11px] text-zinc-500 truncate">{a.reason}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" disabled={busy === a.id} onClick={() => decide(a, true)} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Approve</Button>
                  <Button size="sm" variant="outline" disabled={busy === a.id} onClick={() => decide(a, false)} className="h-7 text-xs">Reject</Button>
                </div>
              </div>
            ))}
        </div>
      </CardContent></Card>

      <Section title="This month" subtitle="per-person attendance summary">
        {!data.month.rows.length ? <Empty>No attendance recorded yet this month.</Empty> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-zinc-500">
              <th className="px-2 py-2 font-normal">Name</th><th className="px-2 py-2 font-normal">Present</th>
              <th className="px-2 py-2 font-normal">Absent</th><th className="px-2 py-2 font-normal">Half day</th><th className="px-2 py-2 font-normal">Hours</th>
            </tr></thead>
            <tbody>{data.month.rows.map((r) => (
              <tr key={r.employee_code} className="border-t border-zinc-100">
                <td className="px-2 py-2">{r.name}</td><td className="px-2 py-2 tabular-nums">{r.present}</td>
                <td className="px-2 py-2 tabular-nums">{r.absent}</td><td className="px-2 py-2 tabular-nums">{r.half_day}</td>
                <td className="px-2 py-2 tabular-nums">{r.hours}</td>
              </tr>))}
            </tbody>
          </table></div>
        )}
      </Section>

      <Section title="People" subtitle="interns ending · birthdays · leave ahead · profile gaps">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-zinc-500 mb-1">Interns nearing 6-month completion</div>
            {ppl.interns_ending.length ? ppl.interns_ending.map((x, i) => <div key={i} className="text-[13px]">{x.name} · ends {dmy(x.ends)} <span className="text-zinc-500">({x.days_left} days)</span></div>) : <Empty>None in the next 45 days.</Empty>}
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">This month</div>
            {ppl.birthdays.length ? ppl.birthdays.map((x, i) => <div key={i} className="text-[13px]">{x.name} · birthday on the {x.date}</div>) : null}
            {ppl.anniversaries.map((x, i) => <div key={i} className="text-[13px]">{x.name} · joined {x.since}</div>)}
            {!ppl.birthdays.length && !ppl.anniversaries.length && <Empty>Nothing this month.</Empty>}
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">Leave in the next 14 days</div>
            {ppl.upcoming_leave.length ? ppl.upcoming_leave.map((x, i) => <div key={i} className="text-[13px]">{x.name} · {x.type} · {dmy(x.from)}–{dmy(x.to)}</div>) : <Empty>Nobody booked off.</Empty>}
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">Incomplete profiles</div>
            {ppl.profile_gaps.length ? ppl.profile_gaps.map((x, i) => <div key={i} className="text-[13px]">{x.name} <span className="text-zinc-500">missing {x.missing.join(", ")}</span></div>) : <Empty>All complete.</Empty>}
          </div>
        </div>
      </Section>

      <Section title="Payroll and holidays" subtitle="monthly payout · next run · upcoming holidays">
        <div className="grid md:grid-cols-3 gap-4">
          <div><div className="text-xs text-zinc-500">Monthly payout</div><div className="text-xl font-semibold mt-0.5">{money(data.payroll.monthly_payout)}</div><div className="text-[11px] text-zinc-400 mt-0.5">{data.payroll.payout_basis === "estimate" ? "estimated from salary structures" : `${data.payroll.payout_slips} payslip${data.payroll.payout_slips === 1 ? "" : "s"} for ${monthName(data.payroll.payout_month)}`}</div></div>
          <div><div className="text-xs text-zinc-500">Next payslip run</div><div className="text-xl font-semibold mt-0.5">{dmy(data.payroll.next_run)}</div></div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">Upcoming holidays</div>
            {data.holidays.length ? data.holidays.map((h, i) => <div key={i} className="text-[13px]">{dmy(h.date)} · {h.name}</div>) : <Empty>None scheduled.</Empty>}
          </div>
        </div>
      </Section>
      <SignIns />
      </div>
  );
}

// Who is actually using the app, and what we are still chasing them for.
// Employees, doctors and contractors together on purpose: it is the same
// follow-up whoever they are, and three lists would mean three things to
// remember to look at.

const FIELD_NAMES = {
  phone: "phone", pan: "PAN", address: "address", dob: "DOB",
  emergency_contact_name: "emergency name",
  emergency_contact_phone: "emergency phone",
  account_holder_name: "account holder", bank_name: "bank",
  bank_account: "account no", bank_ifsc: "IFSC",
};

function SignIns() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/admin/sign-ins")
      .then(({ data }) => { if (live) setRows(data || []); })
      .catch(() => {})
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, []);

  if (!loaded) return null;

  const never = rows.filter((r) => !r.last_seen_at);
  const gaps = rows.filter((r) => !r.profile_complete);
  const shown = filter === "never" ? never : filter === "gaps" ? gaps : rows;

  const chip = (key, label, n) => (
    <button key={key} onClick={() => setFilter(key)}
            data-testid={`signins-${key}`}
            className={`text-xs px-2 py-1 rounded border ${filter === key
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
      {label} ({n})
    </button>
  );

  return (
    <Section title="Sign-ins and missing details"
             subtitle="who has used the app &middot; what we are still waiting on">
      <div className="flex flex-wrap gap-2 mb-3">
        {chip("all", "Everyone", rows.length)}
        {chip("never", "Never signed in", never.length)}
        {chip("gaps", "Details missing", gaps.length)}
      </div>
      <div className="max-h-80 overflow-y-auto border border-zinc-100 rounded">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-left text-xs text-zinc-500">
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
              <th className="px-3 py-2 font-medium">Still needed</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.kind}-${r.id}`} className="border-t border-zinc-100 align-top">
                <td className="px-3 py-2">
                  {r.name}
                  <span className="block text-[11px] text-zinc-400">{r.email}</span>
                </td>
                <td className="px-3 py-2 text-zinc-600">{r.kind}</td>
                <td className={`px-3 py-2 ${r.last_seen_at ? "text-zinc-600" : "text-amber-700"}`}>
                  {since(r.last_seen_at)}
                </td>
                <td className="px-3 py-2 text-[12px]">
                  {r.profile_complete
                    ? <span className="text-emerald-700">complete</span>
                    : <span className="text-amber-700">
                        {r.missing.map((f) => FIELD_NAMES[f] || f).join(", ")}
                      </span>}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-500">
                Nobody in this list.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
